// =====================================================================
// Tri impression de la liste de départ — 07.10.2026 (2e version).
// Le tri est une liste ORDONNÉE de SECTIONS définies par des critères sur
// les champs des concurrents, ex :
//   1. « Women »  : Genre = W
//   2. « Men U18 »: Genre = M  ET  Catégorie = MU18
// Chaque concurrent de la liste de départ va dans la PREMIÈRE section dont
// il remplit tous les critères ; dans une section, l'ordre de départ est
// conservé ; ceux qui ne correspondent à aucune section sont regroupés à
// la fin dans « Others ». Aucune donnée des concurrents n'est modifiée.
//
// config.printSort = { sections: [{ label, rules: [{ field, op, value }] }], pageBreak }
// À placer dans src/utils/.
// =====================================================================

export const OPERATORS = [
    { value: "eq", label: "=" },
    { value: "neq", label: "≠" },
    { value: "contains", label: "contient" },
    { value: "gte", label: "≥" },
    { value: "lte", label: "≤" },
];

// Genre : W/F/L/D/Women/Femmes… -> W ; M/H/Men/Hommes… -> M.
export function normGender(v) {
    const s = String(v ?? "").trim().toUpperCase();
    if (!s) return "";
    if (/^(W|F|L|D)/.test(s)) return "W";
    if (/^(M|H)/.test(s)) return "M";
    return s;
}

const norm = (v) =>
    String(v ?? "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .trim()
        .toLowerCase();

export function matchesRule(athlete, rule) {
    if (!rule || !rule.field) return true;
    const isGender = rule.field === "gender";
    const a = isGender ? normGender(athlete[rule.field]) : norm(athlete[rule.field]);
    const v = isGender ? normGender(rule.value) : norm(rule.value);
    const na = parseFloat(a);
    const nv = parseFloat(v);
    switch (rule.op) {
        case "neq":
            return a !== v;
        case "contains":
            return a.includes(v);
        case "gte":
            return !isNaN(na) && !isNaN(nv) ? na >= nv : a >= v;
        case "lte":
            return !isNaN(na) && !isNaN(nv) ? na <= nv : a <= v;
        case "eq":
        default:
            return a === v;
    }
}

export const matchesSection = (athlete, section) => (section.rules || []).every((r) => matchesRule(athlete, r));

// Ancien format (champ + ordre de valeurs) -> sections.
export function toSections(printSort, categories) {
    if (!printSort) return [];
    if (Array.isArray(printSort.sections)) return printSort.sections;
    const field = printSort.field;
    if (!field) return [];
    const values = field === "category" ? (categories || []).map((c) => c.name) : printSort.orders?.[field] || [];
    return values.map((v) => ({ label: v, rules: [{ field, op: "eq", value: v }] }));
}

// Groupes à afficher/imprimer : [{ category: libellé, entries }].
export function groupBySections(list, printSort, categories) {
    const sections = toSections(printSort, categories).filter((s) => (s.rules || []).some((r) => r.field));
    if (!sections.length) return [{ category: null, entries: list }];
    const buckets = sections.map(() => []);
    const others = [];
    (list || []).forEach((a) => {
        const i = sections.findIndex((s) => matchesSection(a, s));
        if (i === -1) others.push(a);
        else buckets[i].push(a);
    });
    const groups = sections
        .map((s, i) => ({ category: s.label || ruleSummary(s), entries: buckets[i] }))
        .filter((g) => g.entries.length);
    if (others.length) groups.push({ category: "Others", entries: others });
    return groups;
}

export function ruleSummary(section) {
    const lbl = { eq: "=", neq: "≠", contains: "contient", gte: "≥", lte: "≤" };
    return (section.rules || [])
        .filter((r) => r.field)
        .map((r) => `${r.field} ${lbl[r.op] || "="} ${r.value}`)
        .join(" et ");
}